import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Lazy-load pages for code splitting
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const KioskPage = React.lazy(() => import('./pages/KioskPage'));
const WorkoutDisplayPage = React.lazy(() => import('./pages/WorkoutDisplayPage'));
const CoachRemotePage = React.lazy(() => import('./pages/CoachRemotePage'));
const StatusPage = React.lazy(() => import('./pages/StatusPage'));
const AdminLayout = React.lazy(() => import('./pages/AdminLayout'));
const StudentLayout = React.lazy(() => import('./pages/StudentLayout'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const StudentsPage = React.lazy(() => import('./pages/StudentsPage'));
const StudentDetailPage = React.lazy(() => import('./pages/StudentDetailPage'));
const PaymentsPage = React.lazy(() => import('./pages/PaymentsPage'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage'));
const ContractsPage = React.lazy(() => import('./pages/ContractsPage'));
const FormsPage = React.lazy(() => import('./pages/FormsPage'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const EquipmentPage = React.lazy(() => import('./pages/EquipmentPage'));
const ClassesPage = React.lazy(() => import('./pages/ClassesPage'));
const ExerciseLibraryPage = React.lazy(() => import('./pages/ExerciseLibraryPage'));
const ShopPage = React.lazy(() => import('./pages/ShopPage'));
const WebsitePage = React.lazy(() => import('./pages/WebsitePage'));
const MarketingPage = React.lazy(() => import('./pages/MarketingPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const AccountingPage = React.lazy(() => import('./pages/AccountingPage'));
const MartialArtsPage = React.lazy(() => import('./pages/MartialArtsPage'));
const FitnessPage = React.lazy(() => import('./pages/FitnessPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const TimesheetsPage = React.lazy(() => import('./pages/TimesheetsPage'));
const UpgradePage = React.lazy(() => import('./pages/UpgradePage'));
const MessagingPage = React.lazy(() => import('./pages/MessagingPage'));
const LeadsPage = React.lazy(() => import('./pages/LeadsPage'));
const SequencesPage = React.lazy(() => import('./pages/SequencesPage'));
const FamiliesPage = React.lazy(() => import('./pages/FamiliesPage'));
const FreezesPage = React.lazy(() => import('./pages/FreezesPage'));
const GiftCardsPage = React.lazy(() => import('./pages/GiftCardsPage'));
const WebhooksPage = React.lazy(() => import('./pages/WebhooksPage'));
const ClassPackagesPage = React.lazy(() => import('./pages/ClassPackagesPage'));
const AccessProvidersPage = React.lazy(() => import('./pages/AccessProvidersPage'));
const VideoLibraryPage = React.lazy(() => import('./pages/VideoLibraryPage'));
const AdminReferralsPage = React.lazy(() => import('./pages/AdminReferralsPage'));
const DeveloperApiPage = React.lazy(() => import('./pages/DeveloperApiPage'));
const OrgGroupsPage = React.lazy(() => import('./pages/OrgGroupsPage'));
const PromoCodesPage = React.lazy(() => import('./pages/PromoCodesPage'));
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const PayrollPage = React.lazy(() => import('./pages/PayrollPage'));
const WhiteLabelPage = React.lazy(() => import('./pages/WhiteLabelPage'));
const MLInsightsPage = React.lazy(() => import('./pages/MLInsightsPage'));
const StudentDashboard = React.lazy(() => import('./pages/student/StudentDashboard'));
const StudentProfile = React.lazy(() => import('./pages/student/StudentProfile'));
const StudentClasses = React.lazy(() => import('./pages/student/StudentClasses'));
const StudentPrograms = React.lazy(() => import('./pages/student/StudentPrograms'));
const StudentStore = React.lazy(() => import('./pages/student/StudentStore'));
const StudentGradings = React.lazy(() => import('./pages/student/StudentGradings'));
const StudentDocuments = React.lazy(() => import('./pages/student/StudentDocuments'));
const StudentBilling = React.lazy(() => import('./pages/student/StudentBilling'));
const StudentMessages = React.lazy(() => import('./pages/student/StudentMessages'));

// ─── Route Guards ───────────────────────────────────────────────────────────
const RequireAuth = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
};

const RequireStaff = () => {
  const { isStaff, isStudent, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isStudent) return <Navigate to="/student" replace />;
  if (!isStaff) return <Navigate to="/login" replace />;
  return <Outlet />;
};

const RequireStudent = () => {
  const { isStudent, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isStudent) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
};

const RequireAdmin = () => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
};

const RequireModule = ({ module }) => {
  const { hasModule } = useAuth();
  if (!hasModule(module)) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
};

const LoadingScreen = () => (
  <div style={{
    height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#080a0f', fontFamily: "'Outfit', sans-serif",
  }}>
    <div style={{ textAlign: 'center' }}>
      <img src="/logo-64.png" alt="Phoenix Suite" style={{ width: 48, height: 48, marginBottom: 16 }} />
      <div style={{ color: '#8493ad', fontSize: 14 }}>Loading Phoenix Suite...</div>
    </div>
  </div>
);

const AutoRedirect = () => {
  const { isAuthenticated, isStudent, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isStudent) return <Navigate to="/student" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <React.Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/kiosk" element={<KioskPage />} />
            <Route path="/workout-display" element={<WorkoutDisplayPage />} />
            <Route path="/status" element={<StatusPage />} />

            {/* Auto-redirect based on role */}
            <Route path="/" element={<AutoRedirect />} />

            {/* Admin/Staff routes */}
            <Route element={<RequireAuth />}>
              <Route element={<RequireStaff />}>
                <Route element={<AdminLayout />}>
                  <Route path="/upgrade" element={<UpgradePage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/students" element={<StudentsPage />} />
                  <Route path="/students/:id" element={<StudentDetailPage />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/contracts" element={<ContractsPage />} />
                  <Route path="/forms" element={<FormsPage />} />
                  <Route path="/inventory" element={<InventoryPage />} />
                  <Route path="/equipment" element={<EquipmentPage />} />
                  <Route path="/classes" element={<ClassesPage />} />
                  <Route path="/exercises" element={<ExerciseLibraryPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/website" element={<WebsitePage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route element={<RequireAdmin />}>
                    <Route path="/accounting" element={<AccountingPage />} />
                  </Route>
                  <Route element={<RequireModule module="marketing" />}>
                    <Route path="/marketing" element={<MarketingPage />} />
                  </Route>

                  {/* Module-gated routes */}
                  <Route element={<RequireModule module="martial_arts" />}>
                    <Route path="/martial-arts/*" element={<MartialArtsPage />} />
                  </Route>
                  <Route element={<RequireModule module="fitness" />}>
                    <Route path="/fitness/*" element={<FitnessPage />} />
                    <Route path="/coach" element={<CoachRemotePage />} />
                  </Route>

                  {/* Admin-only */}
                  <Route element={<RequireAdmin />}>
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/timesheets" element={<TimesheetsPage />} />
                    <Route path="/messaging" element={<MessagingPage />} />
                    <Route path="/leads" element={<LeadsPage />} />
                    <Route path="/sequences" element={<SequencesPage />} />
                    <Route path="/families" element={<FamiliesPage />} />
                    <Route path="/freezes" element={<FreezesPage />} />
                    <Route path="/gift-cards" element={<GiftCardsPage />} />
                    <Route path="/webhooks" element={<WebhooksPage />} />
                    <Route path="/class-packages" element={<ClassPackagesPage />} />
                    <Route path="/access-providers" element={<AccessProvidersPage />} />
                    <Route path="/videos" element={<VideoLibraryPage />} />
                    <Route path="/referrals" element={<AdminReferralsPage />} />
                    <Route path="/developer" element={<DeveloperApiPage />} />
                    <Route path="/org-groups" element={<OrgGroupsPage />} />
                    <Route path="/promo-codes" element={<PromoCodesPage />} />
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="/payroll" element={<PayrollPage />} />
                    <Route path="/white-label" element={<WhiteLabelPage />} />
                    <Route path="/ai-insights" element={<MLInsightsPage />} />
                  </Route>
                </Route>
              </Route>

              {/* Student routes */}
              <Route element={<RequireStudent />}>
                <Route element={<StudentLayout />}>
                  <Route path="/student" element={<StudentDashboard />} />
                  <Route path="/student/profile" element={<StudentProfile />} />
                  <Route path="/student/classes" element={<StudentClasses />} />
                  <Route path="/student/programs" element={<StudentPrograms />} />
                  <Route path="/student/store" element={<StudentStore />} />
                  <Route path="/student/documents" element={<StudentDocuments />} />
                  <Route path="/student/gradings" element={<StudentGradings />} />
                  <Route path="/student/billing" element={<StudentBilling />} />
                  <Route path="/student/messages" element={<StudentMessages />} />
                </Route>
              </Route>
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
